import React, { memo } from 'react';
import { Handle, Position } from '@xyflow/react';
import { Calendar, Clock } from 'lucide-react';
import { useTheme } from '../ThemeContext';

export const DateTimeNode = memo(({ data, selected }) => {
  const { isDark } = useTheme();
  const slots = data.slots || ['10:00 AM - 12:00 PM', '02:00 PM - 04:00 PM', '05:00 PM - 07:00 PM'];
  const dateOptions = data.dateOptions || ['Today', 'Tomorrow', 'Next Business Day'];

  return (
    <div className={`w-80 rounded-xl border transition-all ${
      isDark ? 'bg-slate-900 border-slate-800 shadow-xl' : 'bg-white border-slate-200 shadow-lg'
    } ${
      selected ? 'border-indigo-500 ring-2 ring-indigo-500/30' : (isDark ? 'hover:border-slate-700' : 'hover:border-slate-300')
    }`}>
      {/* Target Handle */}
      <Handle
        type="target"
        position={Position.Left}
        id="input"
        className={`!h-3.5 !w-3.5 !border-2 !bg-indigo-500 hover:!scale-125 transition-transform ${
          isDark ? '!border-slate-900' : '!border-white'
        }`}
      />

      {/* Node Header */}
      <div className={`flex items-center justify-between rounded-t-xl px-3.5 py-2.5 border-b ${
        isDark
          ? 'bg-gradient-to-r from-indigo-950/80 to-indigo-900/40 border-indigo-500/20'
          : 'bg-gradient-to-r from-indigo-50 to-purple-50 border-indigo-200'
      }`}>
        <div className="flex items-center gap-2">
          <div className={`flex h-6 w-6 items-center justify-center rounded-lg ${
            isDark ? 'bg-indigo-500/20 text-indigo-400' : 'bg-indigo-100 text-indigo-700'
          }`}>
            <Calendar className="h-3.5 w-3.5" />
          </div>
          <span className={`text-xs font-bold uppercase tracking-wider ${
            isDark ? 'text-indigo-200' : 'text-indigo-900'
          }`}>
            {data.label || 'Date & Time Picker'}
          </span>
        </div>
        <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${
          isDark ? 'bg-indigo-500/10 text-indigo-300' : 'bg-indigo-100 text-indigo-700'
        }`}>
          BOOKING
        </span>
      </div>

      {/* Node Body */}
      <div className="p-3.5 space-y-3 text-xs">
        <div className={`font-medium ${isDark ? 'text-slate-300' : 'text-slate-800'}`}>
          {data.prompt || 'Please select your convenient date & time slot for the consultation:'}
        </div>

        {/* Date options preview */}
        <div className="space-y-1">
          <div className={`flex items-center gap-1 text-[10px] font-semibold ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
            <Calendar className="h-3 w-3 text-indigo-500" />
            <span>Dates:</span>
          </div>
          <div className="flex flex-wrap gap-1">
            {dateOptions.map((opt, i) => (
              <span
                key={i}
                className={`rounded px-1.5 py-0.5 text-[10px] font-medium border ${
                  isDark
                    ? 'bg-indigo-950/50 border-indigo-800/40 text-indigo-300'
                    : 'bg-indigo-50 border-indigo-200 text-indigo-800'
                }`}
              >
                {opt}
              </span>
            ))}
          </div>
        </div>

        {/* Time slots preview */}
        <div className="space-y-1">
          <div className={`flex items-center gap-1 text-[10px] font-semibold ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
            <Clock className="h-3 w-3 text-indigo-500" />
            <span>Available Slots:</span>
          </div>
          <div className="space-y-1">
            {slots.map((slot, i) => (
              <div
                key={i}
                className={`flex items-center justify-between rounded px-2 py-1 text-[11px] font-medium border ${
                  isDark
                    ? 'bg-slate-800/60 border-slate-700/60 text-slate-300'
                    : 'bg-slate-50 border-slate-200 text-slate-700'
                }`}
              >
                <span>{slot}</span>
                <span className="text-[9px] text-emerald-500 font-bold">Open</span>
              </div>
            ))}
          </div>
        </div>

        <div className={`flex items-center justify-between border-t pt-2 text-[10px] ${
          isDark ? 'border-slate-800 text-slate-400' : 'border-slate-100 text-slate-500'
        }`}>
          <span>Slot Chosen:</span>
          <Handle
            type="source"
            position={Position.Right}
            id="output"
            className={`!h-3.5 !w-3.5 !border-2 !bg-indigo-500 hover:!scale-125 transition-transform ${
              isDark ? '!border-slate-900' : '!border-white'
            }`}
          />
        </div>
      </div>
    </div>
  );
});
